import React from 'react';
import { AlertCircle, Car, Plus, User } from 'lucide-react';
import { Modal } from '../../common/Modal';

// Extracted verbatim from JobManagement's JSX (Task 8).
// The orchestrator keeps the `show*` guard, so mount/unmount semantics are
// unchanged; this component is the modal's body only.
//
// isOpen is hardcoded true: the orchestrator's `showCreateForm &&` guard
// at the call site is what controls mounting. Because isOpen is never
// false, Modal's `if (!isOpen) return null` can never fire here — so
// dropping the call-site guard would not hide anything, it would leave a
// permanently mounted, permanently VISIBLE modal. The guard must stay.
//
// The "New Job" button that opens this modal already calls resetJobForm()
// before setShowCreateForm(true), so the form is guaranteed empty on every
// open regardless of how the previous instance was dismissed (Cancel,
// header X, or Escape).
const CreateJobModal = ({
  isLoading,
  setShowCreateForm,
  newJob,
  setNewJob,
  resetJobForm,
  createJob
}) => (
        <Modal
          isOpen
          onClose={() => setShowCreateForm(false)}
          title="Create New Job"
          size="lg"
          // Before the Modal conversion this overlay's backdrop had no
          // onClick at all — Cancel and Create were the only exits. A stray
          // backdrop click now discards customer name/phone/email, make,
          // model, year, mileage, trim, VIN, symptom description and
          // priority with no prompt, so backdrop dismissal stays off.
          // Escape remains available as a deliberate two-step exit.
          closeOnOverlayClick={false}
        >
              <div className="space-y-6">
                {/* Customer Information */}
                <div>
                  <h4 className="text-md font-medium text-ink mb-3 flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    Customer Information (Optional)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      placeholder="Customer Name"
                      value={newJob.customer.name}
                      onChange={(e) => setNewJob({
                        ...newJob,
                        customer: { ...newJob.customer, name: e.target.value }
                      })}
                      className="border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      value={newJob.customer.phone}
                      onChange={(e) => setNewJob({
                        ...newJob,
                        customer: { ...newJob.customer, phone: e.target.value }
                      })}
                      className="border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={newJob.customer.email}
                      onChange={(e) => setNewJob({
                        ...newJob,
                        customer: { ...newJob.customer, email: e.target.value }
                      })}
                      className="border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>
                </div>

                {/* Vehicle Information */}
                <div>
                  <h4 className="text-md font-medium text-ink mb-3 flex items-center">
                    <Car className="h-5 w-5 mr-2" />
                    Vehicle Information
                    <span className="ml-2 text-sm font-normal text-ink-muted">(Trim is required for maintenance data, VIN is optional)</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder="Make (e.g., Honda)"
                      value={newJob.vehicle.make}
                      onChange={(e) => setNewJob({
                        ...newJob,
                        vehicle: { ...newJob.vehicle, make: e.target.value }
                      })}
                      className="border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Model (e.g., Civic)"
                      value={newJob.vehicle.model}
                      onChange={(e) => setNewJob({
                        ...newJob,
                        vehicle: { ...newJob.vehicle, model: e.target.value }
                      })}
                      className="border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                      required
                    />
                    <input
                      type="number"
                      placeholder="Year"
                      value={newJob.vehicle.year}
                      onChange={(e) => setNewJob({
                        ...newJob,
                        vehicle: { ...newJob.vehicle, year: e.target.value }
                      })}
                      className="border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="number"
                      placeholder="Mileage"
                      value={newJob.vehicle.mileage}
                      onChange={(e) => setNewJob({
                        ...newJob,
                        vehicle: { ...newJob.vehicle, mileage: e.target.value }
                      })}
                      className="border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    <div>
                      <input
                        type="text"
                        placeholder="Trim (e.g., EX-L, Sport, LX 4dr Sedan CVT)"
                        value={newJob.vehicle.trim}
                        onChange={(e) => setNewJob({
                          ...newJob,
                          vehicle: { ...newJob.vehicle, trim: e.target.value }
                        })}
                        className="border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                        required
                      />
                      <p className="mt-1 text-small text-ink-muted">Required: Vehicle trim level for maintenance schedule lookup</p>
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="VIN (17 characters)"
                        value={newJob.vehicle.vin}
                        onChange={(e) => setNewJob({
                          ...newJob,
                          vehicle: { ...newJob.vehicle, vin: e.target.value }
                        })}
                        maxLength={17}
                        className="border border-border-default rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent font-mono"
                      />
                      <p className="mt-1 text-small text-ink-muted">Optional: Vehicle identification number for precise parts lookup</p>
                    </div>
                  </div>
                </div>

                {/* Symptoms */}
                <div>
                  <h4 className="text-md font-medium text-ink mb-3 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Symptoms & Description
                  </h4>
                  <textarea
                    placeholder="Describe the issue in detail... (e.g., 'Engine making knocking sound when accelerating, started last week')"
                    value={newJob.symptom_description}
                    onChange={(e) => setNewJob({
                      ...newJob,
                      symptom_description: e.target.value
                    })}
                    rows={4}
                    className="w-full border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                    required
                  />
                  <p className="mt-1 text-sm text-ink-muted">
                    Tip: Include when the issue started, conditions when it occurs, and any recent changes or repairs.
                  </p>
                </div>

                {/* Priority */}
                <div>
                  <h4 className="text-md font-medium text-ink mb-3">Priority Level</h4>
                  <select
                    value={newJob.priority}
                    onChange={(e) => setNewJob({ ...newJob, priority: e.target.value })}
                    className="border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="low">Low - Routine maintenance</option>
                    <option value="normal">Normal - Standard repair</option>
                    <option value="high">High - Important issue</option>
                    <option value="urgent">Urgent - Safety concern</option>
                  </select>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="-mx-6 -mb-4 mt-2 px-6 py-4 border-t border-border-default flex justify-end space-x-3">
                <button
                  onClick={() => {
                    resetJobForm();
                    setShowCreateForm(false);
                  }}
                  className="px-4 py-2 border border-border-default rounded-md text-ink hover:bg-surface-sunken"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={createJob}
                  disabled={isLoading || !newJob.symptom_description || !newJob.vehicle.make || !newJob.vehicle.model || !newJob.vehicle.trim}
                  className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Job
                    </>
                  )}
                </button>
              </div>
        </Modal>
);

export default CreateJobModal;
